import { useState, useCallback, useEffect, useRef } from 'react';
import { rootUrl } from '../../../../config/config';

export const useTransMessages = () => {
    // Get auth token from localStorage
    const getAuthToken = () => localStorage.getItem('authToken') || 'default-token';

    // Debounce timer ref
    const debounceTimerRef = useRef(null);

    // State management
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [stats, setStats] = useState({
        total: 0,
        pending: 0,
        posted: 0,
        failed: 0,
        unused: 0,
        totalAmount: 0
    });

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalMessages, setTotalMessages] = useState(0);
    const [pageSize] = useState(25);

    // Sort state
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');

    // Filter state
    const [filters, setFilters] = useState({
        status: '',
        message_type: '',
        car_id: '',
        driver_id: '',
        amount_min: '',
        amount_max: '',
        date_from: '',
        date_to: '',
        content: '',
        posting_status: ''
    });

    // Modal states
    const [showModal, setShowModal] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [retryDialogOpen, setRetryDialogOpen] = useState(false);

    // Notification state
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'info'
    });

    // Pagination object for easier prop passing
    const pagination = {
        currentPage,
        totalPages,
        totalMessages,
        pageSize
    };

    // Utility functions
    const showSnackbar = useCallback((message, severity = 'info') => {
        setSnackbar({ open: true, message, severity });
    }, []);

    const hideSnackbar = useCallback(() => {
        setSnackbar(prev => ({ ...prev, open: false }));
    }, []);

    // API calls
    const fetchMessages = useCallback(async (page = 1, customFilters = null) => {
        try {
            setLoading(true);
            setError(null);

            const filtersToUse = customFilters || filters;

            // Map frontend filter names to backend parameter names
            const backendParams = {
                page: String(page),
                limit: pageSize.toString(),
                search: filtersToUse.content || '',
                status: filtersToUse.status || '',
                type: filtersToUse.message_type || '',
                amountMin: filtersToUse.amount_min || '',
                amountMax: filtersToUse.amount_max || '',
                startDate: filtersToUse.date_from || '',
                endDate: filtersToUse.date_to || '',
                postingStatus: filtersToUse.posting_status || '',
                sortBy,
                sortOrder
            };

            const params = Object.fromEntries(
                Object.entries(backendParams).filter(([_, value]) => value !== '')
            );

            const authToken = getAuthToken();
            const response = await fetch(`${rootUrl}/api/trans-messages?${new URLSearchParams(params)}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                credentials: 'include'
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();

            if (data.success) {
                setMessages(data.messages || []);
                setCurrentPage(data.pagination?.currentPage || page);

                // Only update totalPages if we have pagination data, otherwise preserve current value
                if (data.pagination?.totalPages) {
                    setTotalPages(data.pagination.totalPages);
                }

                setTotalMessages(data.pagination?.totalRecords || 0);

                // Update stats
                if (data.stats) {
                    setStats({
                        total: data.pagination?.totalRecords || 0,
                        pending: data.stats.totalUnused || 0,
                        posted: data.stats.totalPosted || 0,
                        failed: data.stats.totalFailed || 0,
                        unused: data.stats.totalUnused || 0,
                        totalAmount: data.stats.totalAmount || 0
                    });
                }
            } else {
                throw new Error(data.error || data.message || 'Failed to fetch trans messages');
            }
        } catch (err) {
            console.error('Error fetching messages:', err);
            setError('Failed to fetch messages. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [pageSize, filters, sortBy, sortOrder]);

    const handleRetryPosting = async (messageId) => {
        try {
            const authToken = getAuthToken();
            const response = await fetch(`${rootUrl}/api/trans-messages/${messageId}/retry-posting`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                credentials: 'include'
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to retry posting');
            }

            showSnackbar('Message posting retry initiated successfully', 'success');
            // Use debounced fetch to prevent multiple rapid requests
            debouncedFetchForActions();
            setRetryDialogOpen(false);
        } catch (err) {
            console.error('Error retrying posting:', err);
            showSnackbar(err.message || 'Error retrying posting', 'error');
        }
    };

    const handleDeleteMessage = async (messageId) => {
        try {
            const authToken = getAuthToken();
            const response = await fetch(`${rootUrl}/api/trans-messages/${messageId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                credentials: 'include'
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to delete message');
            }

            showSnackbar('Message deleted successfully', 'success');
            // Use debounced fetch to prevent multiple rapid requests
            debouncedFetchForActions();
            setDeleteDialogOpen(false);
        } catch (err) {
            console.error('Error deleting message:', err);
            showSnackbar(err.message || 'Error deleting message', 'error');
        }
    };

    const handleMarkAsUsed = async (messageId) => {
        try {
            const authToken = getAuthToken();
            const response = await fetch(`${rootUrl}/api/trans-messages/${messageId}/mark-used`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                credentials: 'include'
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to mark message as used');
            }

            showSnackbar('Message marked as used', 'success');
            // Use debounced fetch to prevent multiple rapid requests
            debouncedFetchForActions();
        } catch (err) {
            console.error('Error marking message as used:', err);
            showSnackbar(err.message || 'Error marking message as used', 'error');
        }
    };

    const handleRequeueMessage = async (messageId) => {
        try {
            const authToken = getAuthToken();
            const response = await fetch(`${rootUrl}/api/trans-messages/${messageId}/requeue`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                credentials: 'include'
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to requeue message');
            }

            showSnackbar('Message requeued for processing', 'success');
            // Use debounced fetch to prevent multiple rapid requests
            debouncedFetchForActions();
        } catch (err) {
            console.error('Error requeuing message:', err);
            showSnackbar(err.message || 'Error requeuing message', 'error');
        }
    };

    const handleBulkRequeue = async (messageIds) => {
        try {
            const authToken = getAuthToken();
            let successCount = 0;
            let errorCount = 0;

            // Process each message individually
            for (const messageId of messageIds) {
                try {
                    const response = await fetch(`${rootUrl}/api/trans-messages/${messageId}/requeue`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${authToken}`
                        },
                        credentials: 'include'
                    });

                    if (response.ok) {
                        successCount++;
                    } else {
                        errorCount++;
                    }
                } catch {
                    errorCount++;
                }
            }

            if (successCount > 0) {
                showSnackbar(`${successCount} message(s) requeued successfully${errorCount > 0 ? `, ${errorCount} failed` : ''}`,
                    errorCount > 0 ? 'warning' : 'success');
            } else {
                showSnackbar('Failed to requeue messages', 'error');
            }

            // Use debounced fetch to prevent multiple rapid requests
            debouncedFetchForActions();
        } catch (err) {
            console.error('Error in bulk requeue:', err);
            showSnackbar('Error in bulk requeue operation', 'error');
        }
    };

    // Navigation
    const goToPage = useCallback((page) => {
        // Only update currentPage here. The effect that listens to currentPage
        // will call fetchMessages once — avoid calling fetchMessages directly
        // to prevent duplicate requests.
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    }, [totalPages]);

    const handleSortChange = useCallback((field) => {
        setCurrentPage(1);
        if (sortBy === field) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
    }, [sortBy]);

    // Debounced fetch function for actions
    const debouncedFetchForActions = useCallback(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            fetchMessages(currentPage);
        }, 300); // Shorter delay for actions
    }, [fetchMessages, currentPage]);

    // Reset to first page when filters change (with debouncing)
    useEffect(() => {
        setCurrentPage(1);
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            fetchMessages(1);
        }, 500); // 500ms delay
    }, [filters, fetchMessages]);

    // Fetch messages when page changes (immediate, no debouncing needed)
    useEffect(() => {
        fetchMessages(currentPage);
    }, [currentPage, pageSize, fetchMessages]);

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    return {
        // State
        messages,
        loading,
        error,
        stats,
        pagination,
        filters,
        sortBy,
        sortOrder,
        selectedMessage,
        showModal,
        deleteDialogOpen,
        retryDialogOpen,
        snackbar,

        // Actions
        fetchMessages,
        debouncedFetchForActions,
        handleRetryPosting,
        handleDeleteMessage,
        handleMarkAsUsed,
        handleRequeueMessage,
        handleBulkRequeue,
        setFilters,
        handleSortChange,
        setShowModal,
        setDeleteDialogOpen,
        setRetryDialogOpen,
        setSelectedMessage,
        goToPage,
        showSnackbar,
        hideSnackbar
    };
};
